export const MAX_MODERN_FRAME_PATH_LENGTH = 2_048;

const MODERN_PREFIX = '/_modern';
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/;
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+.-]*:/i;
const MALFORMED_PERCENT_ENCODING_PATTERN = /%(?![\da-f]{2})/i;
const ENCODED_SEPARATOR_PATTERN = /%(?:2f|5c)/i;

/** Converts a safe canonical or internal modern-frame URL to its canonical public URL. */
export function toCanonicalModernUrl(value: string): string | null {
  if (
    value.length > MAX_MODERN_FRAME_PATH_LENGTH ||
    CONTROL_CHARACTER_PATTERN.test(value) ||
    ABSOLUTE_URL_PATTERN.test(value) ||
    value.startsWith('//') ||
    value.includes('\\') ||
    MALFORMED_PERCENT_ENCODING_PATTERN.test(value) ||
    ENCODED_SEPARATOR_PATTERN.test(value)
  ) {
    return null;
  }

  const normalized = value.length === 0 ? '/' : value.startsWith('/') ? value : '/' + value;
  const suffixIndex = findSuffixIndex(normalized);
  const pathname = suffixIndex === -1 ? normalized : normalized.slice(0, suffixIndex);
  const suffix = suffixIndex === -1 ? '' : normalized.slice(suffixIndex);

  if (!isSafePathname(pathname)) {
    return null;
  }

  if (pathname === MODERN_PREFIX) {
    return '/' + suffix;
  }

  if (pathname.startsWith(MODERN_PREFIX + '/')) {
    const canonicalPath = pathname.slice(MODERN_PREFIX.length) || '/';
    return canonicalPath + suffix;
  }

  return (pathname || '/') + suffix;
}

function isSafePathname(pathname: string): boolean {
  if (pathname.length > 1 && pathname.includes('//')) return false;

  try {
    return pathname.split('/').every((segment) => {
      const decoded = decodeURIComponent(segment);
      return decoded !== '.' && decoded !== '..' &&
        !decoded.includes('/') && !decoded.includes('\\') &&
        !CONTROL_CHARACTER_PATTERN.test(decoded);
    });
  } catch {
    return false;
  }
}

/** Converts a safe canonical URL to the internal URL served to the modern iframe. */
export function toModernFrameUrl(value: string): string | null {
  const canonicalUrl = toCanonicalModernUrl(value);
  if (canonicalUrl === null) {
    return null;
  }

  return canonicalUrl === '/' ? MODERN_PREFIX + '/' : MODERN_PREFIX + canonicalUrl;
}

/** Returns the pathname portion of a canonical or internal modern-frame URL, or null when invalid. */
export function getModernFramePathname(canonicalUrl: string): string | null {
  const canonical = toCanonicalModernUrl(canonicalUrl);
  if (canonical === null) {
    return null;
  }

  return canonical.split(/[?#]/, 1)[0] ?? null;
}

function findSuffixIndex(value: string): number {
  const queryIndex = value.indexOf('?');
  const fragmentIndex = value.indexOf('#');

  if (queryIndex === -1) {
    return fragmentIndex;
  }

  if (fragmentIndex === -1) {
    return queryIndex;
  }

  return Math.min(queryIndex, fragmentIndex);
}
